import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/NavBar';
import { Sparkles, Bug, Wrench, ArrowLeft, Clock, User } from 'lucide-react';
import { getUpdates } from '../services/api';

function Updates() {
  const navigate = useNavigate();
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    try {
      const data = await getUpdates();
      setUpdates(data);
    } catch (error) {
      console.error('Erreur lors du chargement des updates:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'majeur':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'mineur':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'correctif':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'majeur':
        return '🚀 Majeur';
      case 'mineur':
        return '✨ Mineur';
      case 'correctif':
        return '🔧 Correctif';
      default:
        return type;
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition"
          >
            <ArrowLeft size={20} className="mr-2" />
            Retour
          </button>
          
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            📋 Notes de mise à jour
          </h1>
          <p className="text-gray-600">
            Découvrez les dernières améliorations et nouveautés de Run Clubs
          </p>
        </div>

        {/* Timeline des updates */}
        {updates.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500">Aucune mise à jour disponible pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {updates.map((update, index) => (
              <div
                key={update._id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Header de l'update */}
                <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl font-bold">{update.version}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getTypeColor(update.type)}`}>
                          {getTypeLabel(update.type)}
                        </span>
                      </div>
                      <h2 className="text-2xl font-bold mb-2">{update.titre}</h2>
                      <p className="text-indigo-100">{update.description}</p>
                    </div>
                  </div>
                  
                  {/* Métadonnées */}
                  <div className="flex items-center space-x-4 mt-4 text-sm text-indigo-200">
                    <div className="flex items-center">
                      <Clock size={16} className="mr-1" />
                      {formatDate(update.datePublication)}
                    </div>
                    {update.createur && (
                      <div className="flex items-center">
                        <User size={16} className="mr-1" />
                        {update.createur.prenom} {update.createur.nom}
                      </div>
                    )}
                  </div>
                </div>

                {/* Corps de l'update */}
                <div className="p-6 space-y-6">
                  {/* Nouveautés */}
                  {update.nouveautes && update.nouveautes.length > 0 && (
                    <div>
                      <h3 className="flex items-center text-lg font-semibold text-gray-800 mb-3">
                        <Sparkles className="mr-2 text-purple-600" size={20} />
                        Nouveautés
                      </h3>
                      <ul className="space-y-2">
                        {update.nouveautes.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-purple-600 mr-2 mt-1">✨</span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Améliorations */}
                  {update.ameliorations && update.ameliorations.length > 0 && (
                    <div>
                      <h3 className="flex items-center text-lg font-semibold text-gray-800 mb-3">
                        <Wrench className="mr-2 text-blue-600" size={20} />
                        Améliorations
                      </h3>
                      <ul className="space-y-2">
                        {update.ameliorations.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-blue-600 mr-2 mt-1">🔧</span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Corrections */}
                  {update.corrections && update.corrections.length > 0 && (
                    <div>
                      <h3 className="flex items-center text-lg font-semibold text-gray-800 mb-3">
                        <Bug className="mr-2 text-green-600" size={20} />
                        Corrections de bugs
                      </h3>
                      <ul className="space-y-2">
                        {update.corrections.map((item, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-green-600 mr-2 mt-1">🐛</span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Séparateur si ce n'est pas le dernier */}
                {index < updates.length - 1 && (
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer subtil */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>Run Clubs • Fait avec ❤️ pour la communauté running</p>
        </div>
      </main>
    </div>
  );
}

export default Updates;